/**
 * @fileoverview Feedback Sheet Component
 * 
 * A sheet for users to submit feedback via text, voice, or with attachments.
 */

import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mic, MicOff, Send, Sparkles, MessageSquare, Loader2, ImagePlus, X, Video, Image as ImageIcon } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useFeedback } from '@/hooks/useFeedback';
import { useVoiceInput } from '@/hooks/useVoiceInput';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';

interface FeedbackSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function FeedbackSheet({ open, onOpenChange }: FeedbackSheetProps) {
  const { user } = useAuth();
  const { submitFeedback } = useFeedback();
  const [content, setContent] = useState('');
  const [source, setSource] = useState<'text' | 'voice'>('text');
  const [attachment, setAttachment] = useState<File | null>(null);
  const [attachmentPreview, setAttachmentPreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleTranscript = (text: string) => {
    setContent(prev => prev ? `${prev} ${text}` : text);
    setSource('voice');
  };

  const {
    isRecording,
    isTranscribing,
    liveText,
    toggleRecording,
    cancelRecording,
  } = useVoiceInput(handleTranscript);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const isImage = file.type.startsWith('image/');
    const isVideo = file.type.startsWith('video/');
    
    if (!isImage && !isVideo) {
      toast.error('Please select an image or video file');
      return;
    }

    // Validate file size (max 20MB)
    if (file.size > 20 * 1024 * 1024) {
      toast.error('File must be under 20MB');
      return;
    }

    setAttachment(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setAttachmentPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const removeAttachment = () => {
    setAttachment(null);
    setAttachmentPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async () => {
    if (!content.trim() && !attachment) {
      toast.error('Please enter some feedback or attach a file');
      return;
    }

    if (!user) {
      toast.error('Please sign in to submit feedback');
      return;
    }

    setUploading(true);
    try {
      let attachmentUrl: string | null = null;
      let attachmentType: string | null = null;

      // Upload attachment if present
      if (attachment) {
        const fileExt = attachment.name.split('.').pop();
        const fileName = `${user.id}/${Date.now()}.${fileExt}`;
        
        const { error: uploadError } = await supabase.storage
          .from('feedback-attachments')
          .upload(fileName, attachment);

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('feedback-attachments')
          .getPublicUrl(fileName);

        attachmentUrl = publicUrl;
        attachmentType = attachment.type.startsWith('video/') ? 'video' : 'image';
      }

      await submitFeedback.mutateAsync({
        content: content.trim(),
        source,
        attachmentUrl,
        attachmentType,
      });
      
      toast.success('Thanks for your feedback! 💜', {
        description: "We'll review it soon.",
      });
      setContent('');
      setSource('text');
      removeAttachment();
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      toast.error('Failed to submit feedback');
    } finally {
      setUploading(false);
    }
  };

  const displayText = isRecording ? liveText : content;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent 
        side="bottom" 
        className="rounded-t-3xl px-5 pt-6 pb-4"
      >
        <SheetHeader className="mb-4">
          <SheetTitle className="flex items-center gap-2 text-left">
            <div className="p-2 rounded-xl bg-primary/10">
              <MessageSquare className="w-5 h-5 text-primary" />
            </div>
            Share Your Feedback
          </SheetTitle>
          <p className="text-sm text-muted-foreground text-left hide-on-keyboard">
            Tell us what you love, what could be better, or share feature ideas!
          </p>
        </SheetHeader>

        <div className="space-y-4">
          {/* Attachment Preview */}
          {attachmentPreview && (
            <div className="relative inline-block">
              {attachment?.type.startsWith('video/') ? (
                <video
                  src={attachmentPreview}
                  className="h-24 w-auto rounded-xl object-cover"
                  controls={false}
                />
              ) : (
                <img
                  src={attachmentPreview}
                  alt="Attachment preview"
                  className="h-24 w-auto rounded-xl object-cover"
                />
              )}
              <button
                onClick={removeAttachment}
                className="absolute -top-2 -right-2 p-1 bg-destructive text-destructive-foreground rounded-full shadow-lg"
              >
                <X className="w-3 h-3" />
              </button>
              <div className="absolute bottom-1 left-1 px-1.5 py-0.5 bg-black/60 rounded text-white text-xs flex items-center gap-1">
                {attachment?.type.startsWith('video/') ? (
                  <><Video className="w-3 h-3" /> Video</>
                ) : (
                  <><ImageIcon className="w-3 h-3" /> Image</>
                )}
              </div>
            </div>
          )}

          {/* Input area with live transcription display */}
          <div className="relative">
            <Textarea
              placeholder={isRecording ? "Listening..." : "Type your feedback or tap the mic to dictate..."}
              value={displayText}
              onChange={(e) => {
                setContent(e.target.value);
                setSource('text');
              }}
              disabled={isRecording}
              className={cn(
                "resize-none pr-12 transition-all min-h-[120px]",
                isRecording && "bg-primary/5 border-primary/30"
              )}
            />
            
            {/* Voice indicator */}
            <AnimatePresence>
              {isRecording && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  className="absolute top-2 right-2 flex items-center gap-1.5 px-2 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium"
                >
                  <motion.div
                    animate={{ opacity: [1, 0.5, 1] }}
                    transition={{ duration: 1, repeat: Infinity }}
                    className="w-2 h-2 rounded-full bg-red-500"
                  />
                  Recording
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-2">
            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,video/*"
              onChange={handleFileSelect}
              className="hidden"
            />
            
            {/* Attach button */}
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading || isTranscribing}
              className="shrink-0"
            >
              <ImagePlus className="w-5 h-5" />
            </Button>

            {/* Voice input button */}
            <Button
              type="button"
              variant={isRecording ? "destructive" : "outline"}
              size="icon"
              className={cn(
                "relative overflow-hidden transition-all shrink-0",
                isRecording && "ring-2 ring-red-500/30"
              )}
              onClick={toggleRecording}
              disabled={isTranscribing || uploading}
            >
              {isTranscribing ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : isRecording ? (
                <MicOff className="w-5 h-5" />
              ) : (
                <Mic className="w-5 h-5" />
              )}
              
              {/* Recording pulse animation */}
              {isRecording && (
                <motion.div
                  className="absolute inset-0 bg-red-500/20"
                  animate={{ opacity: [0, 0.3, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                />
              )}
            </Button>

            {/* Submit button */}
            <Button
              onClick={handleSubmit}
              disabled={(!content.trim() && !attachment) || submitFeedback.isPending || isRecording || uploading}
              className="flex-1"
              size="lg"
            >
              {uploading || submitFeedback.isPending ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Send className="w-5 h-5 mr-2" />
                  Send Feedback
                </>
              )}
            </Button>
          </div>

          {/* Helper text - hide when keyboard is visible */}
          <p className="text-xs text-center text-muted-foreground hide-on-keyboard">
            <Sparkles className="w-3 h-3 inline mr-1" />
            Your feedback helps us make WNTD better for everyone
          </p>
        </div>
      </SheetContent>
    </Sheet>
  );
}
